import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

axios.defaults.baseURL = API;
axios.defaults.withCredentials = true;

// Global error interceptor for better user-facing messages
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      // Network error — server unreachable
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.error('Unable to reach the server. Please check your connection and try again.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('The request timed out. Please try again.');
      }
      return Promise.reject(error);
    }

    const status = error.response.status;
    const detail = error.response.data?.detail;

    // Don't auto-toast for auth check (GET /auth/me) — handled by AuthContext
    if (error.config?.url === '/api/auth/me') {
      return Promise.reject(error);
    }

    // Map common status codes to user-friendly messages when no detail is provided
    if (!detail) {
      switch (status) {
        case 400:
          // Let individual handlers provide context
          break;
        case 401:
          toast.error('Your session has expired. Please sign in again.');
          break;
        case 403:
          toast.error('You don\'t have permission to do that.');
          break;
        case 404:
          // Silent — individual handlers deal with this
          break;
        case 413:
          toast.error('That file is too large. Please try a smaller file.');
          break;
        case 429:
          toast.error('Too many requests. Please wait a moment and try again.');
          break;
        case 500:
          toast.error('Something went wrong on our end. Please try again shortly.');
          break;
        case 502:
        case 503:
          toast.error('The server is temporarily unavailable. Please try again in a moment.');
          break;
      }
    }

    return Promise.reject(error);
  }
);

interface User {
  user_id: string;
  email: string;
  name: string;
  picture: string;
  business_name: string | null;
  business_type: string | null;
  state: string | null;
  // The shop's address for weather: engine/weather/shop_location.py.
  location?: { address?: string; label?: string; geocode?: string; source?: string } | null;
  monthly_revenue_range: string | null;
  cogs_percentage: number;
  plan: string;
  subscription_status: string;
  onboarding_completed: boolean;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, consentVersion: string) => Promise<void>;
  logout: () => Promise<void>;
  googleLogin: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const res = await axios.get('/api/auth/me');
      setUser(res.data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/auth/me');
        setUser(res.data);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email: string, password: string) => {
    const res = await axios.post('/api/auth/login', { email, password });
    setUser(res.data);
  };

  const register = async (name: string, email: string, password: string, consentVersion: string) => {
    const res = await axios.post('/api/auth/register', { name, email, password, consent_version: consentVersion });
    setUser(res.data);
  };

  const logout = async () => {
    await axios.post('/api/auth/logout');
    setUser(null);
  };

  const googleLogin = () => {
    window.location.href = `${API}/api/auth/google`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, googleLogin, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
